/**
 * @file stockHaulage.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-29
 * @modified 2026-10-29
 * @description A STOCK purchase collected by a third party — the one rule the
 *              PO editor and the server share (Cam, 29 Oct 2026: "when we
 *              create a PO-D (not from an SO-D) and choose Third Party
 *              Collection for supplier terms, we should get the same offer of
 *              hauliers as in the same situation when creating an SO-D, and a
 *              PO-H should be generated and linked. This is for when we order
 *              something into stock, and get a Third Party to collect for us
 *              and bring here to site.").
 *
 *   A stock purchase is a PO bought for no customer order: no
 *   customer_order_id, no line bought for an order line, no customer of its
 *   own ("None — delivered to us"), not self-billed (a self-billed PO has its
 *   own third-party rule, utils/selfBilledPo) and not a PO-H. On supplier
 *   terms Third-Party Collection it names its haulier (a supplier contact,
 *   as an order on Third-Party Delivery does) and the delivery service the
 *   haulier is booked for (Standard unless chosen); our side of its terms is
 *   Third-Party Delivery (the pair), kept on the PO; and it gets ONE PO-H to
 *   that haulier, collected from the supplier and delivered to our own site
 *   (services/selfBilledHaulageService, which keeps both kinds).
 *
 *   The server's utils/stockHaulage.js is GENERATED from this file by
 *   scripts/sync-shared-utils.js — edit this one and re-run it.
 */

const THIRD_PARTY_SUPPLIER = 'Third-Party Collection';
const THIRD_PARTY_OURS = 'Third-Party Delivery';

const text = (v) => String(v == null ? '' : v).trim();

export const STOCK_HAULIER_REQUIRED_MESSAGE =
  'Choose the third-party haulier who collects it — they are sent a haulage order (PO-H) to bring it to our site.';
export const STOCK_HAULAGE_HINT =
  'Collected by a third party for our stock: the haulier is sent a haulage order (PO-H) to collect from the supplier and bring it to our site.';
export const STOCK_OUR_TERMS_HINT =
  'A stock PO collected by a third party: they deliver it to our site, so our side is Third-Party Delivery.';

/**
 * Is this PO a stock purchase — bought for no customer order and no customer,
 * not self-billed, not a PO-H?
 * @param {object} p
 * @param {boolean} [p.selfBilled]
 * @param {string}  [p.poType]            'haulage' for a PO-H
 * @param {boolean} [p.forCustomerOrder]  raised from / bought for a customer order
 *                                        (utils/poTransportLock.poHasCustomerOrder)
 * @param {*}       [p.customerId]        the PO's own customer, if any
 */
export function isStockPurchase({ selfBilled = false, poType = null, forCustomerOrder = false, customerId = null } = {}) {
  if (selfBilled) return false;
  if (text(poType) === 'haulage') return false;
  if (forCustomerOrder) return false;
  if (text(customerId) !== '') return false;
  return true;
}

/** A stock purchase on Third-Party Collection: it names its haulier and gets a PO-H of its own. */
export function stockThirdParty(p = {}) {
  return isStockPurchase(p) && text(p.supplierTerms) === THIRD_PARTY_SUPPLIER;
}

/** Our side of a stock PO's terms: Third-Party Delivery with Third-Party Collection (the pair), else none. */
export function stockOurTerms(supplierTerms) {
  return text(supplierTerms) === THIRD_PARTY_SUPPLIER ? THIRD_PARTY_OURS : null;
}

/**
 * Why a PO on Third-Party Collection gets no haulier or PO-H of its own here,
 * or null (it is not on Third-Party Collection, or it does get one). A
 * self-billed PO and a PO-H are other rules' and say nothing here.
 */
export function stockHaulageBlock({ selfBilled = false, poType = null, forCustomerOrder = false, customerId = null, supplierTerms = null } = {}) {
  if (text(supplierTerms) !== THIRD_PARTY_SUPPLIER || selfBilled || text(poType) === 'haulage') return null;
  if (forCustomerOrder) {
    return 'This PO is for a customer order — its haulier and haulage order (PO-H) are the order\'s (Third-Party Delivery on the order).';
  }
  if (text(customerId) !== '') {
    return 'This PO names a customer, so it is not a stock purchase — set the customer to "None — delivered to us" for a haulier and PO-H of its own, or raise a customer order on Third-Party Delivery.';
  }
  return null;
}

/**
 * Why a stock PO's PO-H is no longer wanted — the words the history and the
 * cancellation offer give ('' when it still is).
 * @param {object} p  as stockThirdParty, plus haulierId and archived
 */
export function stockHaulageEndReason({ archived = false, forCustomerOrder = false, customerId = null, supplierTerms = null, haulierId = null } = {}) {
  if (archived) return 'archived';
  if (forCustomerOrder) return 'now for a customer order';
  if (text(customerId) !== '') return 'now for a customer';
  if (text(supplierTerms) !== THIRD_PARTY_SUPPLIER) return 'no longer third-party collection';
  if (!haulierId) return 'no haulier chosen';
  return '';
}
